import React from 'react';
import type { UseDevOptionsReturn } from '../hooks/useDevOptions';

type DevOptionsPanelProps = Pick<
  UseDevOptionsReturn,
  | 'isDevMode'
  | 'devOptionsExpanded'
  | 'setDevOptionsExpanded'
  | 'useTextRecall'
  | 'setUseTextRecall'
  | 'pageSize'
  | 'setPageSize'
  | 'rankingWeights'
  | 'handleWeightChange'
  | 'useTargetedSectionTitles'
  | 'setUseTargetedSectionTitles'
  | 'useTwoPhaseFiltering'
  | 'setUseTwoPhaseFiltering'
  | 'useOutlierFiltering'
  | 'setUseOutlierFiltering'
  | 'ignoreRatings'
  | 'setIgnoreRatings'
  | 'ratingThreshold'
  | 'setRatingThreshold'
  | 'llmModel'
  | 'setLlmModel'
  | 'llmModelLight'
  | 'setLlmModelLight'
  | 'searchTemperature'
  | 'setSearchTemperature'
  | 'maxPerBrand'
  | 'setMaxPerBrand'
  | 'maxProductsForLLM'
  | 'setMaxProductsForLLM'
  | 'numSearchAngles'
  | 'setNumSearchAngles'
  | 'targetCategories'
  | 'setTargetCategories'
  | 'semanticThreshold'
  | 'setSemanticThreshold'
  | 'productsPerSection'
  | 'setProductsPerSection'
  | 'productsBuffer'
  | 'setProductsBuffer'
  | 'angleStrategy'
  | 'setAngleStrategy'
  | 'availabilityFilter'
  | 'setAvailabilityFilter'
  | 'useOriginalQueryReranking'
  | 'setUseOriginalQueryReranking'
  | 'recatModel'
  | 'setRecatModel'
  | 'analyzeModel'
  | 'setAnalyzeModel'
  | 'planModel'
  | 'setPlanModel'
  | 'promptLabel'
  | 'setPromptLabel'
>;

/** Known LLM model options for dropdowns */
const HEAVY_MODELS = ['gpt-5.2', 'gpt-4o', 'gpt-4o-mini', 'gpt-4.1-mini'];
const LIGHT_MODELS = ['gpt-4o-mini', 'gpt-4.1-mini', 'gpt-4o', 'gpt-5.2'];
/** Recat model options — empty string means "same as heavy" */
const RECAT_MODELS = ['', 'gpt-4o-mini', 'gpt-4.1-mini', 'gpt-4o', 'gpt-5.2'];
/** Analyze/Plan model options — empty string means "same as light" */
const ANALYZE_PLAN_MODELS = ['', 'gpt-4o-mini', 'gpt-4.1-mini', 'gpt-4o', 'gpt-5.2'];

/** Reusable toggle button pair */
const TogglePair: React.FC<{
  label: string;
  value: boolean;
  onLabel: string;
  offLabel: string;
  onToggle: (v: boolean) => void;
  disabled?: boolean;
}> = ({ label, value, onLabel, offLabel, onToggle, disabled }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: disabled ? 0.4 : 1 }}>
    <span style={{ color: '#555', fontWeight: 500 }}>{label}:</span>
    {[true, false].map((v) => (
      <button
        key={String(v)}
        onClick={() => !disabled && onToggle(v)}
        disabled={disabled}
        style={{
          padding: '4px 14px',
          borderRadius: '6px',
          border: '1px solid',
          borderColor: value === v ? '#2563eb' : '#d1d5db',
          backgroundColor: value === v ? '#eff6ff' : 'transparent',
          color: value === v ? '#2563eb' : '#6b7280',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '13px',
          fontWeight: value === v ? 600 : 400,
        }}
      >
        {v ? onLabel : offLabel}
      </button>
    ))}
  </div>
);

/** Reusable numeric input */
const NumericInput: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  width?: string;
  disabled?: boolean;
}> = ({ label, value, onChange, min = 1, max = 999, step = 1, width = '72px', disabled }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: disabled ? 0.4 : 1 }}>
    <span style={{ color: '#555', fontWeight: 500 }}>{label}:</span>
    <input
      type="number"
      value={value}
      onChange={(e) => {
        const parsed = step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value);
        if (!isNaN(parsed)) onChange(Math.max(min, Math.min(max, parsed)));
      }}
      disabled={disabled}
      step={step}
      style={{
        width,
        padding: '4px 8px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '13px',
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'text',
      }}
    />
  </div>
);

/** Reusable multi-pill toggle group (like TogglePair but supports N options) */
const ToggleGroup: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ label, value, options, onChange, disabled }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: disabled ? 0.4 : 1 }}>
    <span style={{ color: '#555', fontWeight: 500 }}>{label}:</span>
    {options.map((opt) => (
      <button
        key={opt.value}
        onClick={() => !disabled && onChange(opt.value)}
        disabled={disabled}
        style={{
          padding: '4px 14px',
          borderRadius: '6px',
          border: '1px solid',
          borderColor: value === opt.value ? '#2563eb' : '#d1d5db',
          backgroundColor: value === opt.value ? '#eff6ff' : 'transparent',
          color: value === opt.value ? '#2563eb' : '#6b7280',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '13px',
          fontWeight: value === opt.value ? 600 : 400,
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

/** Reusable select dropdown */
const SelectInput: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{ color: '#555', fontWeight: 500 }}>{label}:</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '4px 8px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '13px',
        cursor: 'pointer',
        background: 'white',
      }}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

/** Collapsible dev-only options panel for tuning concierge search behavior.
 *  Renders nothing when `isDevMode` is false. */
const DevOptionsPanel: React.FC<DevOptionsPanelProps> = ({
  isDevMode,
  devOptionsExpanded,
  setDevOptionsExpanded,
  useTextRecall,
  setUseTextRecall,
  pageSize,
  setPageSize,
  rankingWeights,
  handleWeightChange,
  useTargetedSectionTitles,
  setUseTargetedSectionTitles,
  useTwoPhaseFiltering,
  setUseTwoPhaseFiltering,
  useOutlierFiltering,
  setUseOutlierFiltering,
  ignoreRatings,
  setIgnoreRatings,
  ratingThreshold,
  setRatingThreshold,
  llmModel,
  setLlmModel,
  llmModelLight,
  setLlmModelLight,
  searchTemperature,
  setSearchTemperature,
  maxPerBrand,
  setMaxPerBrand,
  maxProductsForLLM,
  setMaxProductsForLLM,
  numSearchAngles,
  setNumSearchAngles,
  targetCategories,
  setTargetCategories,
  semanticThreshold,
  setSemanticThreshold,
  productsPerSection,
  setProductsPerSection,
  productsBuffer,
  setProductsBuffer,
  angleStrategy,
  setAngleStrategy,
  availabilityFilter,
  setAvailabilityFilter,
  useOriginalQueryReranking,
  setUseOriginalQueryReranking,
  recatModel,
  setRecatModel,
  analyzeModel,
  setAnalyzeModel,
  planModel,
  setPlanModel,
  promptLabel,
  setPromptLabel,
}) => {
  if (!isDevMode) return null;

  return (
    <div style={{ borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
      <button
        onClick={() => setDevOptionsExpanded((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '6px 16px',
          background: '#fefce8',
          border: 'none',
          cursor: 'pointer',
          fontSize: '13px',
          color: '#92400e',
          fontWeight: 500,
        }}
      >
        <span>Dev Options</span>
        <span style={{ fontSize: '12px' }}>{devOptionsExpanded ? '\u25B2' : '\u25BC'}</span>
      </button>
      {devOptionsExpanded && (
        <div
          style={{
            padding: '12px 16px',
            fontSize: '13px',
            color: '#555',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            background: '#fffef5',
          }}
        >
          {/* Section: Search Pipeline Toggles */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <TogglePair label="Recall" value={useTextRecall} onLabel="Text" offLabel="KNN" onToggle={setUseTextRecall} />
            <TogglePair label="Sections" value={useTargetedSectionTitles} onLabel="Targeted" offLabel="Legacy" onToggle={setUseTargetedSectionTitles} />
            <TogglePair label="KNN" value={useTwoPhaseFiltering} onLabel="2-Phase" offLabel="PostFilter" onToggle={setUseTwoPhaseFiltering} />
            <TogglePair label="Outlier Filter" value={useOutlierFiltering} onLabel="On" offLabel="Off" onToggle={setUseOutlierFiltering} />
            <TogglePair label="Ratings" value={ignoreRatings} onLabel="Ignore" offLabel="Filter" onToggle={setIgnoreRatings} />
            <ToggleGroup
              label="Search Angles"
              value={angleStrategy}
              options={[
                { value: 'brand', label: 'Brand' },
                { value: 'generic', label: 'Generic' },
                { value: 'auto', label: 'Auto' },
              ]}
              onChange={(v) => setAngleStrategy(v as 'brand' | 'generic' | 'auto')}
            />
            <TogglePair label="Rerank" value={useOriginalQueryReranking} onLabel="On" offLabel="Off" onToggle={setUseOriginalQueryReranking} />
            <ToggleGroup
              label="Availability"
              value={availabilityFilter}
              options={[
                { value: 'apply', label: 'Apply' },
                { value: 'skipFalse', label: 'Skip False' },
                { value: 'skipAll', label: 'Skip All' },
              ]}
              onChange={(v) => setAvailabilityFilter(v as 'apply' | 'skipFalse' | 'skipAll')}
            />
          </div>

          {/* Section: LLM Models */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <SelectInput label="LLM (heavy)" value={llmModel} options={HEAVY_MODELS} onChange={setLlmModel} />
            <SelectInput label="LLM (light)" value={llmModelLight} options={LIGHT_MODELS} onChange={setLlmModelLight} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#555', fontWeight: 500 }}>Recat:</span>
              <select
                value={recatModel}
                onChange={(e) => setRecatModel(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  cursor: 'pointer',
                  background: 'white',
                }}
              >
                {RECAT_MODELS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || `(same as heavy)`}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#555', fontWeight: 500 }}>Analyze:</span>
              <select
                value={analyzeModel}
                onChange={(e) => setAnalyzeModel(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  cursor: 'pointer',
                  background: 'white',
                }}
              >
                {ANALYZE_PLAN_MODELS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || `(same as light)`}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#555', fontWeight: 500 }}>Plan:</span>
              <select
                value={planModel}
                onChange={(e) => setPlanModel(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  cursor: 'pointer',
                  background: 'white',
                }}
              >
                {ANALYZE_PLAN_MODELS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || `(same as light)`}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#555', fontWeight: 500 }}>Prompt:</span>
              <input
                type="text"
                value={promptLabel}
                onChange={(e) => setPromptLabel(e.target.value)}
                placeholder="production"
                style={{
                  width: '120px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                }}
              />
            </div>
          </div>

          {/* Section: Numeric Parameters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <NumericInput label="Angles" value={numSearchAngles} onChange={setNumSearchAngles} min={1} max={12} />
            <NumericInput label="Categories" value={targetCategories} onChange={setTargetCategories} min={1} max={10} />
            <NumericInput label="Max/brand" value={maxPerBrand} onChange={setMaxPerBrand} min={1} max={20} />
            <NumericInput label="Max LLM" value={maxProductsForLLM} onChange={setMaxProductsForLLM} min={10} max={200} />
            <NumericInput label="Page size" value={pageSize} onChange={setPageSize} min={1} max={200} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <NumericInput label="Per section" value={productsPerSection} onChange={setProductsPerSection} min={1} max={50} />
            <NumericInput label="Buffer" value={productsBuffer} onChange={setProductsBuffer} min={1} max={50} disabled={!useOutlierFiltering} />
            <NumericInput
              label="Rating min"
              value={ratingThreshold}
              onChange={setRatingThreshold}
              min={0}
              max={5}
              step={0.1}
              width="72px"
              disabled={ignoreRatings}
            />
            <NumericInput label="Temp" value={searchTemperature} onChange={setSearchTemperature} min={0} max={1} step={0.1} width="72px" />
            <NumericInput label="Semantic" value={semanticThreshold} onChange={setSemanticThreshold} min={0} max={1} step={0.05} width="72px" />
          </div>

          {/* Ranking Weights with sliders */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ color: '#555', fontWeight: 500 }}>
              Ranking weights{' '}
              <span style={{ fontWeight: 400, color: '#999' }}>
                (total: {Object.values(rankingWeights).reduce((s, v) => s + v, 0).toFixed(2)})
              </span>
            </span>
            {(Object.keys(rankingWeights) as Array<keyof typeof rankingWeights>).map((key) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ minWidth: '70px', color: '#666', fontSize: '13px' }}>
                  {key.replace('Weight', '')}
                </span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={rankingWeights[key]}
                  onChange={(e) => handleWeightChange(key, parseFloat(e.target.value))}
                  style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer' }}
                />
                <span
                  style={{
                    minWidth: '36px',
                    textAlign: 'right',
                    fontSize: '13px',
                    color: '#444',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {rankingWeights[key].toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DevOptionsPanel;
